import React, { useMemo } from 'react'
import { Paper, Box, Typography } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { BRANDS, IInvoiceWithCustomer } from './statistics'

interface RevenueChartProps {
  invoices: IInvoiceWithCustomer[]
}

function formatMonth(month: string) {
  // month: '2025-07' => 'Jul 2025'
  const [year, m] = month.split('-')
  const date = new Date(Number(year), Number(m) - 1)
  return date.toLocaleString('id-ID', { month: 'short', year: 'numeric' })
}
export default function RevenueChart({ invoices }: RevenueChartProps) {
  // Calculate revenue per month, separated by brand
  const data = useMemo(() => {
    if (!Array.isArray(invoices) || invoices.length === 0) return []
    // Brand keys
    const monthMap: { [month: string]: { [brand: string]: number } } = {}
    let minMonth: string | null = null
    let maxMonth: string | null = null
    invoices.forEach((invoice) => {
      let createdAt = invoice.createdAt
      if (!createdAt) return

      let total = invoice.total ?? invoice.grandTotal ?? invoice.amount ?? 0
      total = Number(total)
      if (isNaN(total)) total = 0
      const month = String(createdAt).slice(0, 7) // 'YYYY-MM'
      let brand = 'Other'
      brand = invoice.brandId
      if (!BRANDS.includes(brand)) brand = 'Other'
      if (!monthMap[month])
        monthMap[month] = { WLW: 0, Lipoceutics: 0, Other: 0 }
      monthMap[month][brand] = (monthMap[month][brand] || 0) + total
      if (!minMonth || month < minMonth) minMonth = month
      if (!maxMonth || month > maxMonth) maxMonth = month
    })
    if (!minMonth || !maxMonth) return []
    // Generate all months between minMonth and maxMonth
    const months: string[] = []
    const [minY, minM] = (minMonth as string).split('-').map(Number)
    const [maxY, maxM] = (maxMonth as string).split('-').map(Number)
    let y = minY,
      m = minM
    while (y < maxY || (y === maxY && m <= maxM)) {
      const mm = m < 10 ? `0${m}` : `${m}`
      months.push(`${y}-${mm}`)
      m++
      if (m > 12) {
        m = 1
        y++
      }
    }
    months.sort((a, b) => a.localeCompare(b))
    // Return array of { month, ...brands, Other }
    return months.map((month) => {
      const row: any = { month: formatMonth(month) }
      BRANDS.forEach((brand) => {
        row[brand] = monthMap[month]?.[brand] || 0
      })
      row['Other'] = monthMap[month]?.Other || 0
      return row
    })
  }, [invoices])

  return (
    <Paper style={{ marginTop: 32 }}>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Revenue per Bulan (IDR)
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 48, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => v.toLocaleString('id-ID')} />
            <Tooltip formatter={(v: any) => v.toLocaleString('id-ID')} />
            {/* Dynamically render a line for each brand in BRANDS, plus Other */}
            {Array.isArray(BRANDS)
              ? BRANDS.filter((b) => b !== 'Other').map((brand, i) => (
                  <Line
                    key={brand}
                    type="monotone"
                    dataKey={brand}
                    stroke={
                      brand === 'WLW'
                        ? '#82ca9d'
                        : brand === 'Lipoceutics'
                        ? '#8884d8'
                        : `hsl(${(i * 60) % 360},70%,50%)`
                    }
                    strokeWidth={2}
                    name={brand}
                  />
                ))
              : null}
            <Line
              type="monotone"
              dataKey="Other"
              stroke="#ff9800"
              strokeWidth={2}
              name="Other Brands"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  )
}
